import { Accordion, AccordionDetails, AccordionSummary, Avatar, Stack, Chip } from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { GenericTable } from "../../../../../components/GenericTable/GenericTable";
import { useSessionContext } from "../../../../../contexts/SessionContext";
import { ProblemsActions } from "./ProblemsActions";

// Mapa de cores para os status das condutas
const statusColorMap: Record<string, string> = {
  "Realizada": "#007C6D",
  "Concluído": "#007C6D",
  "Parcialmente": "#DB7712", // Amarelo mais escuro para melhor contraste
  "Não realizada": "#EF4352",
  "Falhou": "#EF4352",
};

interface ConductItem {
  encounterId: string | undefined;
  encounterRef: string | undefined;
  description: string; // texto da conduta
  status: string | null; // display do valueCoding (se houver)
  date: string;
  dateObj: Date;
  practitioner: string;
}

export function ProblemsConducts({ title, personData, openVisualizer, verifyVisualizer }: any) {
  const { access } = useSessionContext();

  const columns = [
    { column: "Descrição", rowName: "description" },
    { column: "Status", rowName: "statusDisplay" },
    { column: "Profissional", rowName: "practitioner" },
    { column: "Data", rowName: "date" },
  ];

  function groupConducts(personData: any[]): Record<string, ConductItem[]> {
    const groups: Record<string, ConductItem[]> = {};
    if (!personData?.length) return groups;
  
    personData.forEach((entry: any) => {
      if (entry?.resource?.resourceType !== "QuestionnaireResponse") return;
  
      const resource = entry.resource;
      const encounterRef = resource?.encounter?.reference;
      const encounterId = encounterRef?.split("/")?.[1];
      const authored = resource?.authored;
  
      // Seção de condutas
      const conductsSection = resource?.item?.find((section: any) => section?.text === "Condutas");
      if (!conductsSection?.item) return;
  
      // Informações de exibição (profissional)
      const displayReferences = resource?.item?.find((i: any) => i?.linkId === "displayReferences")?.item;
      const practitioner = (displayReferences?.[1]?.answer?.[0]?.valueString || "").toUpperCase();
  
      conductsSection.item.forEach((cond: any) => {
        const description = cond?.item?.[0]?.answer?.[0]?.valueString;
        if (!description) return;
  
        const key = description.trim().toLowerCase();
        
        // Extrai status do valueCoding (prioriza display, depois code)
        const statusCoding = cond?.item?.[1]?.answer?.[0]?.valueCoding;
        const status = statusCoding?.display || statusCoding?.code || null;
  
        const item: ConductItem = {
          encounterId,
          encounterRef,
          description,
          status,
          date: authored,
          dateObj: new Date(authored),
          practitioner,
        };
  
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });
    });
  
    return groups;
  }
  
  /**
   * Aplica lógica par/ímpar para seleção de condutas
   * - PAR: mostra TODOS os registros que têm status (com seus devidos status)
   * - ÍMPAR: mostra TODOS os registros que têm status + 1 sem status (mais recente)
   */
  function applyEvenOddLogic(groups: Record<string, ConductItem[]>): ConductItem[] {
    const result: ConductItem[] = [];
  
    Object.values(groups).forEach((items) => {
      if (items.length === 1) {
        result.push(items[0]);
        return;
      }
  
      // Ordena por data (mais recente primeiro)
      const sorted = items.sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());
      
      const isPair = items.length % 2 === 0;
      const withStatus = sorted.filter((i) => i.status !== null && i.status !== undefined);
      const withoutStatus = sorted.filter((i) => i.status === null || i.status === undefined);
  
      if (isPair) {
        // PAR: mostra TODOS os registros que TÊM status
        if (withStatus.length > 0) {
          withStatus.forEach(item => result.push(item));
        } else {
          // Se nenhum tiver status, mostra apenas o mais recente
          result.push(sorted[0]);
        }
      } else {
        // ÍMPAR: mostra TODOS os registros com status + 1 sem status
        if (withStatus.length > 0) {
          // Adiciona TODOS os que têm status
          withStatus.forEach(item => result.push(item));
          
          // E adiciona o mais recente sem status (se houver)
          if (withoutStatus.length > 0) {
            result.push(withoutStatus[0]);
          }
        } else {
          // Se nenhum tiver status, mostra apenas o mais recente
          result.push(sorted[0]);
        }
      }
    });
  
    return result;
  }

  // 1) Agrupa condutas por descrição
  const grouped = groupConducts(personData);

  // 2) Aplica lógica par/ímpar (par -> 1 item, ímpar -> até 2: com status e sem status)
  const conducts = applyEvenOddLogic(grouped)
    .sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());

  return (
    <Accordion sx={{ width: "100%" }} elevation={5}>
      <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
        <Stack direction="row" alignItems="center" gap={1}>
          {title}
          <Avatar
            sx={{
              width: 26,
              height: 26,
              fontSize: "14px",
              fontWeight: 600,
              backgroundColor: "primary100.main",
              color: "primary1000.main",
            }}
          >
            {conducts.length || "0"}
          </Avatar>
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <GenericTable
          tableColumns={columns}
          tableRows={conducts.map((c) => {
            const statusText = c.status || "Sem status";
            const statusColor = statusColorMap[statusText] || "#9E9E9E"; // Cinza para status não mapeado
            
            return {
              description: c.description,
              statusDisplay: (
                <Chip
                  label={statusText}
                  size="small"
                  sx={{
                    backgroundColor: statusColor,
                    color: "#FFFFFF",
                    fontWeight: 500,
                    fontSize: 12,
                  }}
                />
              ),
              practitioner: (c.practitioner || "").toUpperCase(),
              date: dayjs(c.dateObj).format("DD/MM/YYYY HH:mm"),
              encounterId: c.encounterId,
              encounterRef: c.encounterRef,
            };
          })}
          actions={
            access?.type === "patient"
              ? undefined
              : (row: any) => (
                  <ProblemsActions
                    row={{
                      encounterId: row?.encounterId,
                      encounterRef: row?.encounterRef,
                      isCustom: true, // Condutas sempre vêm de questionários
                    }}
                    openVisualizer={openVisualizer}
                    verifyVisualizer={verifyVisualizer}
                  />
                )
          }
        />
      </AccordionDetails>
    </Accordion>
  );
}




